import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import currentPortalStore from "../../../../stores/current-portal-store";
import policyListStore from "../../stores/policy-list-store";
import ListLayout from "../../../../components/layouts/list-layout/list-layout";
import CategoryCard from "../policy-category-card/policy-category-card";
import { POLICY_RULE_CATEGORY } from "../../policy-types";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import { ExpandCallapseStore } from "../../../../hooks/expand-collapse-hook";
import { useNavigate, useOutletContext } from "react-router-dom";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import { FONT_COLOR, FONT_VARIANT } from "../../../../components/fonts/font-props";
import { PolicyRuleCategoryIcon } from "../../../../components/policy-rule-category-icon/policy-rule-category-icon";
import './policy-list.scss';
import { REFUND_POLICIES_NOTE_TYPE } from "../../constants";
import AHref from "../../../../components/ahref/ahref";
import config from "../../../../config";
import {Note, NOTE_TYPE} from "../../../../components/note/note";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import NoPolicyRules from "./no-policy-rules/no-policy-rules";
import { Button } from "../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import IconPlus from "../../../../components/icons/icon-plus/icon-plus";
import rmsMixpanelStore from "../../../../stores/rms-mixpanel-store";
import { POLICY_RULES_MIXPANEL_ADD_RULE } from "../../metrics/policy-rules-mixpanel-events";

const POLICY_RULE_CATEGORY_TO_TITLE_MAP = {
    [POLICY_RULE_CATEGORY.ORDER_MODIFICATION]: 'Order Modification',
    [POLICY_RULE_CATEGORY.EXCHANGE]: 'Exchange',
    [POLICY_RULE_CATEGORY.RETURN]: 'Return',
}

const PlolicyList: React.FC = observer(() => {
    const navigate = useNavigate();
    const expandCollapseStore = useOutletContext<ExpandCallapseStore>();

    const { setSubTitles } = usePage();
    useEffect(() => {
        setSubTitles(['Return Policy']);
    }, [setSubTitles]);

    const storeSettingsLink = `${config.apiUrl}/dashboard/settings/store`;
    const refundToPaymentMethodNoteData = policyListStore.getRefundToPaymentMethodNoteData();
    const refundToPaymentMethodNoteTextMap = {
        [REFUND_POLICIES_NOTE_TYPE.SET_DISPLAY_AS_DISABLED as REFUND_POLICIES_NOTE_TYPE]: (
            <div>
                Select a “Refund to Payment Method” policy rule to be displayed as disabled when items are ineligible.
                To disable this feature, go to
                <AHref href={storeSettingsLink} target="_parent">
                    <FontBody2 color={FONT_COLOR.ACCENT1}>Settings {'>'} Store settings</FontBody2>
                </AHref> and change the settings
            </div>
        ),
        [REFUND_POLICIES_NOTE_TYPE.CREATE_REFUND_TO_PAYMENT_METHOD as REFUND_POLICIES_NOTE_TYPE]: (
            <div>
                Create a “Refund to Payment Method” policy rule and set it to be displayed as disabled when items are ineligible. 
                This feature can be disabled on the 
                <AHref href={storeSettingsLink} target="_parent">
                    <FontBody2 color={FONT_COLOR.ACCENT1}>Store Settings</FontBody2> 
                </AHref> page.
            </div>
        ),
    }
    const hasPolicies = !!policyListStore.policyList.length;
    const onAddPolicyClicked = () => {
        rmsMixpanelStore.trackEvent(POLICY_RULES_MIXPANEL_ADD_RULE);
        const policyId = policyListStore.addPolicy();
        navigate({
            pathname: policyId.toString()
        });
    }

    const getActiveRulesInCategory = (category: POLICY_RULE_CATEGORY): string => {
        const activeRulesCount = policyListStore.policyListByCategory[category].filter(r => r.isEnabled).length;
        return `${activeRulesCount} Active Rule${activeRulesCount !== 1 ? 's' : ''}`;
    };
    return (
        <EditorPageLayout
            toolbar={{
                hasLocalesSelector: false,
                hasBack: false,
                isLoading: policyListStore.isLoading,
                expandAllButton: (hasPolicies ? {
                    expanded: expandCollapseStore.isAllExpanded(),
                    onClick: expandCollapseStore.toggleAllExpanded,
                } : undefined),
                content: <div className="add-policy-wrapper"><Button variant={BUTTON_VARIANTS.Text} leftIcon={IconPlus} text="Add Rule" onClick={onAddPolicyClicked} disabled={policyListStore.isLoading}/></div>
            }}
        >
            <ListLayout>
                {(policyListStore.isLoaded && !hasPolicies)
                    ? <NoPolicyRules onAddPolicyClicked={onAddPolicyClicked} />
                    : (<>
                        <FontBody1>The following policy rules will apply to all customers, orders, RMAs, and items.</FontBody1>
                        {refundToPaymentMethodNoteData &&
                            <Note
                                type={refundToPaymentMethodNoteData.type}
                                body={refundToPaymentMethodNoteTextMap[refundToPaymentMethodNoteData.bodyType]}
                            />
                        }
                        
                        {
                            currentPortalStore.setting?.switchToExchangeStore.eligibleReturnReasons.length !== 0 &&
                            policyListStore.policyListByCategory?.[POLICY_RULE_CATEGORY.EXCHANGE]?.filter(r => r.isEnabled)?.length === 0 &&
                            !!currentPortalStore.setting?.switchToExchangeStore.isActive &&
                            <Note type={NOTE_TYPE.WARNING}  body={'The Switch to Exchange feature is enabled for ' + currentPortalStore.setting?.switchToExchangeStore.eligibleReturnReasons.length + ' return reason' + ( currentPortalStore.setting?.switchToExchangeStore.eligibleReturnReasons.length === 1 ? '' : 's') + ', but there are no active exchange policy rules. You must enable at least one policy rule that supports exchanges in order to offer customers to switch to exchange.'}/>
                        }
                        {Object.keys(policyListStore.policyListByCategory).map((category) => {
                            return <CategoryCard
                                key={category}
                                isExpanded={expandCollapseStore.getIsExpanded(category)}
                                onExpandClick={(v: boolean) => expandCollapseStore.setExpanded(category, v)}
                                policyList={policyListStore.policyListByCategory[category as POLICY_RULE_CATEGORY]}
                                title={<div className="policy-list-category-name">
                                    <PolicyRuleCategoryIcon category={category as POLICY_RULE_CATEGORY} />
                                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{POLICY_RULE_CATEGORY_TO_TITLE_MAP[category as POLICY_RULE_CATEGORY]}</FontBody1>
                                    <FontBody2 color={FONT_COLOR.PRIMARY3_50}>{getActiveRulesInCategory(category as POLICY_RULE_CATEGORY)}</FontBody2>
                                </div>}
                            />
                        })}
                    </>)
                }
            </ListLayout>
        </EditorPageLayout>
    )
});

export default PlolicyList;
